if (!customElements.get('c-bundle')) {
  class CBundle extends HTMLElement {
    connectedCallback() {
      this.destroyed = false;
      this.busy = false;
      this.picks = [];
      this.mode = this.dataset.mode === 'product' ? 'product' : 'tiers';
      this.required = parseInt(this.dataset.required, 10) || 0;
      this.max = parseInt(this.dataset.max, 10) || 0;
      this.cap = this.mode === 'product' ? this.required : this.max;
      this.requireTier = this.dataset.requireTier === 'true';
      this.moneyFormat = this.dataset.moneyFormat || '{{amount}}';
      this.sectionId = this.dataset.sectionId || '';
      this.bundleVariant = this.dataset.bundleVariant || '';
      this.bundlePrice = parseInt(this.dataset.bundlePrice, 10) || 0;
      this.storageKey = 'c-bundle:' + this.sectionId;
      this.root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

      this.cards = {};
      this.querySelectorAll('.c-bundle__card[data-variant-id]').forEach((card) => {
        const id = card.dataset.variantId;
        if (id && !this.cards[id]) {
          this.cards[id] = {
            id: id,
            price: parseInt(card.dataset.price, 10) || 0,
            name: card.dataset.name || '',
            sub: card.dataset.sub || '',
            image: card.dataset.image || '',
            available: card.dataset.available !== 'false'
          };
        }
      });

      this.tiers = [];
      this.querySelectorAll('.c-bundle__tier').forEach((el) => {
        const condition = el.dataset.condition === 'amount' ? 'amount' : 'items';
        const raw = parseFloat(el.dataset.threshold) || 0;
        const value = parseFloat(el.dataset.value) || 0;
        const reward = el.dataset.reward || 'percent';
        this.tiers.push({
          el: el,
          condition: condition,
          threshold: condition === 'amount' ? Math.round(raw * 100) : raw,
          reward: reward,
          value: reward === 'fixed' ? Math.round(value * 100) : value,
          gift: el.dataset.gift || '',
          giftName: el.dataset.giftName || '',
          code: (el.dataset.code || '').trim(),
          offer: (el.dataset.offer || '').trim(),
          label: (el.dataset.label || '').trim()
        });
      });
      this.sortedTiers = this.tiers.slice().sort((a, b) => a.threshold - b.threshold);

      this.summary = this.querySelector('.c-bundle__summary');
      this.slotEls = Array.prototype.slice.call(this.querySelectorAll('.c-bundle__slot'));
      this.dotEls = Array.prototype.slice.call(this.querySelectorAll('.c-bundle__dot'));
      this.countEl = this.querySelector('.c-bundle__count');
      this.miniCountEl = this.querySelector('.c-bundle__mini-count');
      this.toggleBtn = this.querySelector('.c-bundle__toggle');
      this.submitBtn = this.querySelector('.c-bundle__submit');
      this.statusEl = this.querySelector('.c-bundle__status');
      this.errorEl = this.querySelector('.c-bundle__error');
      this.fullEl = this.querySelector('.c-bundle__full');
      this.totalEl = this.querySelector('.c-bundle__total');
      this.wasEl = this.querySelector('.c-bundle__was');
      this.saveEl = this.querySelector('.c-bundle__save');
      this.barFill = this.querySelector('.c-bundle__bar-fill');
      this.progressText = this.querySelector('.c-bundle__progress-text');

      this.onClick = (e) => this.handleClick(e);
      this.onKeydown = (e) => this.handleKeydown(e);
      this.addEventListener('click', this.onClick);
      this.addEventListener('keydown', this.onKeydown);

      this.split = this.dataset.split !== 'false';
      this.searchInput = this.querySelector('.c-bundle__search-input');
      this.searchClear = this.querySelector('.c-bundle__search-clear');
      this.searchMore = this.querySelector('.c-bundle__search-more');
      this.searchResults = this.querySelector('.c-bundle__search-results');
      this.searchSeq = 0;
      if (this.searchInput) {
        let t = null;
        this.searchInput.addEventListener('input', () => {
          clearTimeout(t);
          t = setTimeout(() => this.filter(this.searchInput.value), 200);
        });
        // Enter searches right away (and never submits a surrounding form).
        this.searchInput.addEventListener('keydown', (e) => {
          if (e.key !== 'Enter') return;
          e.preventDefault();
          clearTimeout(t);
          this.filter(this.searchInput.value);
        });
      }

      this.restore();
      this.render();
    }

    disconnectedCallback() {
      this.destroyed = true;
      if (this.onClick) this.removeEventListener('click', this.onClick);
      if (this.onKeydown) this.removeEventListener('keydown', this.onKeydown);
      if (this.onOutside) document.removeEventListener('pointerdown', this.onOutside, true);
      if (this.onEsc) document.removeEventListener('keydown', this.onEsc);
    }

    // Mobile summary sheet: open/close, close on tap outside or Escape.
    setExpanded(open) {
      if (!this.summary) return;
      this.summary.setAttribute('data-expanded', open ? 'true' : 'false');
      if (this.toggleBtn) this.toggleBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
      const toggleText = this.toggleBtn && this.toggleBtn.querySelector('.c-bundle__toggle-text');
      if (toggleText) toggleText.textContent = open ? 'Hide bundle' : 'View bundle';
      if (open && !this.onOutside) {
        this.onOutside = (e) => {
          if (this.summary.getAttribute('data-expanded') === 'true' && !this.summary.contains(e.target)) this.setExpanded(false);
        };
        this.onEsc = (e) => {
          if (e.key === 'Escape' && this.summary.getAttribute('data-expanded') === 'true') {
            this.setExpanded(false);
            if (this.toggleBtn) this.toggleBtn.focus();
          }
        };
        document.addEventListener('pointerdown', this.onOutside, true);
        document.addEventListener('keydown', this.onEsc);
      }
    }

    restore() {
      try {
        const raw = window.sessionStorage.getItem(this.storageKey);
        if (!raw) return;
        const ids = JSON.parse(raw);
        if (!Array.isArray(ids)) return;
        ids.forEach((entry) => {
          // Entries are ids, or full items for products added from store-wide search.
          const item = entry && typeof entry === 'object' ? entry : null;
          const key = String(item ? item.id : entry);
          if (!this.cards[key] && item && item.id && item.price >= 0) this.cards[key] = Object.assign({ available: true }, item);
          if (this.cards[key] && this.cards[key].available && this.picks.length < this.cap) {
            this.picks.push(this.cards[key]);
          }
        });
      } catch (err) { /* storage unavailable */ }
    }

    persist() {
      try {
        window.sessionStorage.setItem(this.storageKey, JSON.stringify(this.picks.map((p) => (
          this.querySelector('.c-bundle__panel-products [data-variant-id="' + p.id + '"]') ? p.id : { id: p.id, price: p.price, name: p.name, sub: p.sub, image: p.image }
        ))));
      } catch (err) { /* storage unavailable */ }
    }

    handleClick(e) {
      const btn = e.target && e.target.closest ? e.target.closest('button') : null;
      if (!btn || !this.contains(btn)) return;
      if (btn.getAttribute('aria-disabled') === 'true') {
        this.setStatus('Your bundle is full. Remove an item to add another.');
        return;
      }
      if (btn.classList.contains('c-bundle__add') || btn.classList.contains('c-bundle__inc')) {
        const card = btn.closest('.c-bundle__card');
        if (card) this.addPick(card.dataset.variantId);
      } else if (btn.classList.contains('c-bundle__dec')) {
        const card = btn.closest('.c-bundle__card');
        if (card) this.removeUnit(card.dataset.variantId);
      } else if (btn.classList.contains('c-bundle__remove')) {
        if (btn.dataset.id) this.removeUnit(btn.dataset.id);
        else this.removePick(parseInt(btn.dataset.index, 10));
      } else if (btn.classList.contains('c-bundle__search-clear')) {
        this.searchInput.value = '';
        this.filter('');
        this.searchInput.focus();
      } else if (btn.classList.contains('c-bundle__clear')) {
        this.picks = [];
        this.setStatus('Bundle cleared');
        this.setError('');
        this.persist();
        this.render();
      } else if (btn.classList.contains('c-bundle__toggle')) {
        this.setExpanded(this.summary.getAttribute('data-expanded') !== 'true');
      } else if (btn.classList.contains('c-bundle__close')) {
        this.setExpanded(false);
        if (this.toggleBtn) this.toggleBtn.focus();
      } else if (btn.classList.contains('c-bundle__submit')) {
        this.submit();
      } else if (btn.getAttribute('role') === 'tab') {
        this.selectTab(btn, false);
      }
    }

    handleKeydown(e) {
      const tab = e.target && e.target.closest ? e.target.closest('[role="tab"]') : null;
      if (!tab) return;
      const tabs = Array.prototype.slice.call(this.querySelectorAll('[role="tab"]'));
      const i = tabs.indexOf(tab);
      let next = -1;
      if (e.key === 'ArrowRight') next = (i + 1) % tabs.length;
      else if (e.key === 'ArrowLeft') next = (i - 1 + tabs.length) % tabs.length;
      else if (e.key === 'Home') next = 0;
      else if (e.key === 'End') next = tabs.length - 1;
      if (next < 0) return;
      e.preventDefault();
      this.selectTab(tabs[next], true);
    }

    // Search: match cards in every tab, then add matching products from the whole store
    // (Shopify predictive search) as extra cards that can be added to the bundle.
    filter(raw) {
      const q = String(raw || '').trim().toLowerCase();
      const tabs = this.querySelector('.c-bundle__tabs');
      const panels = Array.prototype.slice.call(this.querySelectorAll('.c-bundle__panel-products'));
      const empty = this.querySelector('.c-bundle__search-empty');
      const status = this.querySelector('.c-bundle__search-status');
      const loading = this.querySelector('.c-bundle__search-loading');
      const seq = ++this.searchSeq;
      if (this.searchClear) this.searchClear.hidden = !q;
      if (loading) loading.hidden = true;
      if (!q) {
        if (tabs) tabs.hidden = false;
        panels.forEach((p) => p.querySelectorAll('.c-bundle__card, .c-bundle__empty').forEach((c) => { c.hidden = false; }));
        const sel = this.querySelector('[role="tab"][aria-selected="true"]') || this.querySelector('[role="tab"]');
        if (sel) this.selectTab(sel, false);
        if (empty) empty.hidden = true;
        if (status) status.textContent = '';
        if (this.searchMore) this.searchMore.hidden = true;
        return;
      }
      if (tabs) tabs.hidden = true;
      const words = q.split(/\s+/);
      const seen = {};
      const seenHandles = {};
      let shown = 0;
      panels.forEach((p) => {
        let inPanel = 0;
        p.querySelectorAll('.c-bundle__card').forEach((c) => {
          const text = ((c.dataset.name || '') + ' ' + c.textContent).toLowerCase();
          const id = c.dataset.variantId;
          const match = words.every((w) => text.indexOf(w) !== -1) && !seen[id];
          c.hidden = !match;
          if (match) {
            seen[id] = true;
            inPanel += 1;
          }
          if (c.dataset.handle) seenHandles[c.dataset.handle] = true;
        });
        p.querySelectorAll('.c-bundle__empty').forEach((e) => { e.hidden = true; });
        p.hidden = inPanel === 0;
        shown += inPanel;
      });
      const storeWide = this.searchResults && q.length >= 2;
      // While the store search runs, say so instead of "no products".
      if (empty) empty.hidden = shown > 0 || storeWide;
      if (loading) loading.hidden = !storeWide;
      if (status) status.textContent = storeWide ? 'Searching the store' : shown + (shown === 1 ? ' product found' : ' products found');
      if (storeWide) this.searchStore(q, seq, seenHandles, shown);
      else if (this.searchMore) this.searchMore.hidden = true;
    }

    // The theme's own search (same results as the search page), one request through the
    // custom-bundle-search data section: products with variant prices and photos.
    async searchStore(q, seq, localHandles, localShown) {
      let products = [];
      try {
        const url = this.root + 'search?q=' + encodeURIComponent(q) +
          '&type=product&options[prefix]=last&options[unavailable_products]=hide&section_id=custom-bundle-search';
        const res = await fetch(url);
        const text = res.ok ? await res.text() : '';
        const m = text.match(/<script[^>]*data-c-bundle-search[^>]*>([\s\S]*?)<\/script>/);
        products = m ? JSON.parse(m[1]) : [];
      } catch (err) {
        products = [];
      }
      if (seq !== this.searchSeq || this.destroyed) return;
      const loading = this.querySelector('.c-bundle__search-loading');
      if (loading) loading.hidden = true;
      products = products.filter((p) => p.handle && !localHandles[p.handle] && p.variants.some((v) => v.available));
      const html = products.map((p) => this.cardsFor(p, p.images || {}));
      this.searchResults.innerHTML = html.join('');
      this.searchResults.querySelectorAll('.c-bundle__card[data-variant-id]').forEach((card) => {
        const id = card.dataset.variantId;
        if (id && !this.cards[id]) {
          this.cards[id] = {
            id: id,
            price: parseInt(card.dataset.price, 10) || 0,
            name: card.dataset.name || '',
            sub: card.dataset.sub || '',
            image: card.dataset.image || '',
            available: card.dataset.available !== 'false'
          };
        }
      });
      const more = this.searchResults.children.length;
      this.searchMore.hidden = more === 0;
      const empty = this.querySelector('.c-bundle__search-empty');
      if (empty) empty.hidden = localShown + more > 0;
      const status = this.querySelector('.c-bundle__search-status');
      if (status) status.textContent = (localShown + more) + ' products found';
      this.render();
    }

    async loadProduct(handle) {
      this.productCache = this.productCache || {};
      if (!this.productCache[handle]) {
        this.productCache[handle] = Promise.all([
          fetch(this.root + 'products/' + handle + '.js').then((r) => (r.ok ? r.json() : null)),
          fetch(this.root + 'products/' + handle + '?section_id=custom-variant-image-map')
            .then((r) => (r.ok ? r.text() : ''))
            .then((t) => {
              const m = t.match(/<script[^>]*data-c-vimg-map[^>]*>([\s\S]*?)<\/script>/);
              return m ? JSON.parse(m[1]) : {};
            })
            .catch(() => ({}))
        ]).then(([product, images]) => (product ? { product, images } : null)).catch(() => null);
      }
      return this.productCache[handle];
    }

    cardsFor(product, images) {
      const esc = (t) => String(t == null ? '' : t).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
      const multi = product.variants.length > 1 && !(product.variants.length === 1 && product.variants[0].title === 'Default Title');
      const variants = this.split && multi ? product.variants : [product.variants.find((v) => v.available) || product.variants[0]];
      const fallback = product.featured_image ? product.featured_image : '';
      return variants.filter((v) => v && v.available).map((v) => {
        const name = this.split && multi ? v.title : product.title;
        const sub = this.split && multi ? product.title : '';
        let img = images[String(v.id)] || fallback;
        if (img && img.indexOf('width=') === -1) img += (img.indexOf('?') === -1 ? '?' : '&') + 'width=400';
        const label = esc(name) + (sub ? ' (' + esc(sub) + ')' : '');
        return '<li class="c-bundle__card" data-variant-id="' + v.id + '" data-handle="' + esc(product.handle) + '" data-price="' + v.price +
          '" data-name="' + esc(name) + '" data-sub="' + esc(sub) + '" data-image="' + esc(img) + '" data-available="true">' +
          '<div class="c-bundle__media">' + (img ? '<img class="c-bundle__img" src="' + esc(img) + '" alt="' + esc(product.title) + '" loading="lazy" width="400" height="400">' : '') + '</div>' +
          '<div class="c-bundle__info"><h3 class="c-bundle__name">' + esc(name) + '</h3>' + (sub ? '<p class="c-bundle__sub">' + esc(sub) + '</p>' : '') +
          '<div class="c-bundle__row"><span class="c-bundle__price">' + esc(this.formatMoney(v.price)) + '</span>' +
          '<button type="button" class="c-bundle__add" aria-label="Add ' + label + ' to bundle">+</button>' +
          '<div class="c-bundle__stepper" role="group" aria-label="Quantity of ' + label + '" hidden>' +
          '<button type="button" class="c-bundle__dec" aria-label="Remove one ' + label + '"><span aria-hidden="true">&minus;</span></button>' +
          '<span class="c-bundle__qty">0</span>' +
          '<button type="button" class="c-bundle__inc" aria-label="Add one more ' + label + '"><span aria-hidden="true">+</span></button>' +
          '</div></div></div></li>';
      }).join('');
    }

    selectTab(tab, focus) {
      this.querySelectorAll('[role="tab"]').forEach((t) => {
        const on = t === tab;
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        t.setAttribute('tabindex', on ? '0' : '-1');
        const panel = document.getElementById(t.getAttribute('aria-controls'));
        if (panel) panel.hidden = !on;
      });
      if (focus) tab.focus();
      if (tab.scrollIntoView) {
        try { tab.scrollIntoView({ block: 'nearest', inline: 'nearest' }); } catch (err) { /* ignore */ }
      }
    }

    addPick(id) {
      const item = this.cards[String(id)];
      if (!item || !item.available || this.busy) return;
      if (this.picks.length >= this.cap) {
        this.setStatus('Your bundle is full');
        return;
      }
      this.picks.push(item);
      const qty = this.countOf(item.id);
      this.setError('');
      this.setStatus('Added ' + item.name + (qty > 1 ? ', quantity ' + qty : '') + '. ' + this.picks.length + ' of ' + this.cap + ' picked.');
      this.persist();
      this.render();
    }

    removePick(index) {
      if (isNaN(index) || index < 0 || index >= this.picks.length || this.busy) return;
      const removed = this.picks.splice(index, 1)[0];
      this.setStatus('Removed ' + removed.name + '. ' + this.picks.length + ' of ' + this.cap + ' picked.');
      this.persist();
      this.render();
    }

    countOf(id) {
      let n = 0;
      this.picks.forEach((p) => { if (p.id === String(id)) n += 1; });
      return n;
    }

    removeUnit(id) {
      if (this.busy) return;
      for (let i = this.picks.length - 1; i >= 0; i--) {
        if (this.picks[i].id === String(id)) {
          const removed = this.picks.splice(i, 1)[0];
          const qty = this.countOf(id);
          this.setStatus('Removed one ' + removed.name + ', quantity ' + qty + '. ' + this.picks.length + ' of ' + this.cap + ' picked.');
          this.persist();
          this.render();
          return;
        }
      }
    }

    setStatus(msg) { if (this.statusEl) this.statusEl.textContent = msg; }
    setError(msg) { if (this.errorEl) this.errorEl.textContent = msg; }

    sum() {
      return this.picks.reduce((t, p) => t + p.price, 0);
    }

    activeTier() {
      const count = this.picks.length;
      const total = this.sum();
      let active = null;
      this.sortedTiers.forEach((t) => {
        const current = t.condition === 'amount' ? total : count;
        if (current >= t.threshold) active = t;
      });
      return active;
    }

    tierLabel(t) {
      if (t.label) return t.label;
      if (t.reward === 'percent') return t.value + '% off';
      if (t.reward === 'fixed') return this.formatMoney(t.value) + ' off';
      if (t.reward === 'free') return t.value + (t.value === 1 ? ' item' : ' items') + ' free';
      return 'a free gift' + (t.giftName ? ' (' + t.giftName + ')' : '');
    }

    isValid() {
      const count = this.picks.length;
      if (this.mode === 'product') {
        return !!this.bundleVariant && count === this.required;
      }
      if (count < 1 || count > this.cap) return false;
      if (this.requireTier && this.tiers.length > 0) return !!this.activeTier();
      return true;
    }

    render() {
      if (this.destroyed) return;
      const count = this.picks.length;
      const total = this.sum();

      // One tile per product with a quantity badge; empty tiles show only the units still free.
      const groups = [];
      const byId = {};
      this.picks.forEach((p) => {
        if (!byId[p.id]) {
          byId[p.id] = { item: p, qty: 0 };
          groups.push(byId[p.id]);
        }
        byId[p.id].qty += 1;
      });
      const freeUnits = Math.max(0, this.cap - count);
      this.slotEls.forEach((li, i) => {
        const group = groups[i];
        const pick = group ? group.item : null;
        const thumb = li.querySelector('.c-bundle__thumb');
        const rm = li.querySelector('.c-bundle__remove');
        let qtyEl = li.querySelector('.c-bundle__slot-qty');
        if (!qtyEl) {
          qtyEl = document.createElement('span');
          qtyEl.className = 'c-bundle__slot-qty';
          qtyEl.setAttribute('aria-hidden', 'true');
          li.appendChild(qtyEl);
        }
        qtyEl.textContent = group && group.qty > 1 ? 'x' + group.qty : '';
        qtyEl.hidden = !(group && group.qty > 1);
        li.hidden = !group && i >= groups.length + freeUnits;
        const num = li.querySelector('.c-bundle__slot-num');
        if (num && !group) num.textContent = String(count + (i - groups.length) + 1);
        li.classList.toggle('is-filled', !!pick);
        if (thumb) {
          if (pick && pick.image) {
            thumb.src = pick.image;
            thumb.alt = pick.name;
            thumb.hidden = false;
          } else {
            thumb.removeAttribute('src');
            thumb.alt = '';
            thumb.hidden = true;
          }
        }
        if (rm) {
          rm.hidden = !pick;
          rm.dataset.index = String(i);
          if (pick) rm.dataset.id = pick.id;
          else delete rm.dataset.id;
          rm.setAttribute('aria-label', pick ? (group.qty > 1 ? 'Remove one ' + pick.name + ', quantity ' + group.qty : 'Remove ' + pick.name) : 'Remove item ' + (i + 1));
        }
      });
      this.dotEls.forEach((d, i) => d.classList.toggle('is-on', i < count));

      const counts = {};
      this.picks.forEach((p) => { counts[p.id] = (counts[p.id] || 0) + 1; });
      const full = count >= this.cap;
      this.querySelectorAll('.c-bundle__card').forEach((card) => {
        const n = counts[card.dataset.variantId] || 0;
        const add = card.querySelector('.c-bundle__add');
        const stepper = card.querySelector('.c-bundle__stepper');
        const qtyEl = card.querySelector('.c-bundle__qty');
        const inc = card.querySelector('.c-bundle__inc');
        const dec = card.querySelector('.c-bundle__dec');
        card.classList.toggle('is-picked', n > 0);
        if (add) add.hidden = n > 0;
        if (stepper) stepper.hidden = n === 0;
        if (qtyEl) qtyEl.textContent = String(n);
        [add, inc].forEach((b) => {
          if (!b || b.disabled) return;
          if (full) {
            b.setAttribute('aria-disabled', 'true');
            if (this.fullEl && this.fullEl.id) b.setAttribute('aria-describedby', this.fullEl.id);
          } else {
            b.removeAttribute('aria-disabled');
            b.removeAttribute('aria-describedby');
          }
        });
        if (dec) dec.disabled = this.busy;
      });
      if (this.fullEl) this.fullEl.hidden = !full;

      if (this.countEl) this.countEl.textContent = count + (count === 1 ? ' item' : ' items');
      if (this.miniCountEl) this.miniCountEl.textContent = count + '/' + this.cap;

      let shown;
      let est;
      if (this.mode === 'product') {
        shown = this.bundlePrice;
        est = this.bundlePrice;
      } else {
        const active = this.activeTier();
        est = total;
        if (active && active.reward === 'percent') est = Math.round(total * (1 - active.value / 100));
        else if (active && active.reward === 'fixed') est = Math.max(0, total - active.value);
        else if (active && active.reward === 'free') {
          // Buy X get Y: the cheapest Y picked items are free (once per order).
          const cheapest = this.picks.map((p) => p.price).sort((a, b) => a - b).slice(0, Math.max(0, Math.round(active.value)));
          est = Math.max(0, total - cheapest.reduce((s, v) => s + v, 0));
        }
        shown = est;
      }
      if (this.totalEl) this.totalEl.textContent = this.formatMoney(shown);
      const save = total - est;
      if (this.wasEl) {
        const showWas = count > 0 && save > 0;
        this.wasEl.hidden = !showWas;
        this.wasEl.textContent = showWas ? this.formatMoney(total) : '';
      }
      if (this.saveEl) {
        const showSave = count > 0 && save > 0;
        this.saveEl.hidden = !showSave;
        this.saveEl.textContent = showSave ? 'You save ' + this.formatMoney(save) : '';
      }

      this.renderTiers(count, total);

      if (this.submitBtn) {
        this.submitBtn.disabled = this.busy || !this.isValid();
      }
    }

    renderTiers(count, total) {
      if (this.mode !== 'tiers' || this.tiers.length === 0) return;
      const active = this.activeTier();
      let next = null;
      for (let i = 0; i < this.sortedTiers.length; i++) {
        const t = this.sortedTiers[i];
        const current = t.condition === 'amount' ? total : count;
        if (current < t.threshold) { next = t; break; }
      }
      this.tiers.forEach((t) => {
        const current = t.condition === 'amount' ? total : count;
        t.el.textContent = this.tierLabel(t);
        t.el.classList.toggle('is-reached', current >= t.threshold);
      });
      if (this.barFill) {
        let pct = 100;
        if (next) {
          const cur = next.condition === 'amount' ? total : count;
          pct = Math.max(0, Math.min(100, Math.round((cur / next.threshold) * 100)));
        }
        this.barFill.style.width = pct + '%';
      }
      if (this.progressText) {
        let text;
        if (next) {
          const cur = next.condition === 'amount' ? total : count;
          if (next.condition === 'amount') {
            text = 'Spend ' + this.formatMoney(next.threshold - cur) + ' more to unlock ' + this.tierLabel(next);
          } else {
            const n = next.threshold - cur;
            text = 'Add ' + n + ' more to unlock ' + this.tierLabel(next);
          }
        } else if (active) {
          text = 'Unlocked: ' + this.tierLabel(active);
        } else {
          text = '';
        }
        this.progressText.textContent = text;
      }
    }

    async submit() {
      if (this.busy || !this.isValid()) return;
      this.busy = true;
      this.setError('');
      if (this.submitBtn) {
        this.submitBtn.disabled = true;
        this.submitBtn.setAttribute('aria-busy', 'true');
        if (window.CVayveCartFx && window.CVayveCartFx.early) window.CVayveCartFx.early(this.submitBtn);
      }
      try {
        let items = [];
        let code = '';
        if (this.mode === 'product') {
          const list = this.picks.map((p, i) => (i + 1) + '. ' + p.name + (p.sub ? ' — ' + p.sub : '')).join(', ');
          items.push({
            id: Number(this.bundleVariant),
            quantity: 1,
            properties: { 'Selected items': list, _bundle: this.sectionId }
          });
        } else {
          const agg = {};
          const order = [];
          this.picks.forEach((p) => {
            if (!agg[p.id]) { agg[p.id] = 0; order.push(p.id); }
            agg[p.id] += 1;
          });
          const active = this.activeTier();
          // Ready-made offers: tag the lines so the Vayve discounts app applies the offer
          // (one group id per bundle add, so separate bundles are judged separately).
          const props = { _bundle: this.sectionId };
          if (active && active.offer) {
            props._vayve_offer = active.offer;
            props._vayve_group = this.sectionId + '-' + Date.now().toString(36);
          }
          items = order.map((id) => ({ id: Number(id), quantity: agg[id], properties: props }));
          if (active) {
            if (active.gift) {
              items.push({ id: Number(active.gift), quantity: 1, properties: { _bundle_gift: 'true' } });
            }
            if (!active.offer) code = active.code;
          }
        }

        const res = await fetch(this.root + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' },
          body: JSON.stringify({ items: items })
        });
        let data = {};
        try { data = await res.json(); } catch (err) { data = {}; }
        if (!res.ok || data.status) {
          this.setError(data.description || data.message || 'Could not add the bundle to your cart. Please try again.');
          return;
        }
        if (code) {
          // Attach the code to the cart itself (shows in cart drawer/page and carries to checkout).
          // Keep any non-bundle codes the shopper already applied; replace older bundle codes.
          try {
            const cartRes = await fetch(this.root + 'cart.js', { headers: { Accept: 'application/json' } });
            const cart = cartRes.ok ? await cartRes.json() : {};
            const existing = (cart.discount_codes || [])
              .map((d) => d.code)
              .filter((c) => c && !/^BUNDLE/i.test(c) && c.toUpperCase() !== code.toUpperCase());
            await fetch(this.root + 'cart/update.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' },
              body: JSON.stringify({ discount: existing.concat(code).join(',') })
            });
          } catch (err) {
            try { await fetch(this.root + 'discount/' + encodeURIComponent(code), { credentials: 'same-origin' }); } catch (e) { /* ignore */ }
          }
        }
        this.picks = [];
        this.persist();
        this.setStatus('Bundle added to your cart');
        document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: true } }));
        if (!document.querySelector('m-cart-drawer')) {
          window.location.href = this.root + 'cart';
        }
      } catch (err) {
        this.setError('Something went wrong. Please try again.');
      } finally {
        this.busy = false;
        if (this.submitBtn) this.submitBtn.removeAttribute('aria-busy');
        this.render();
      }
    }

    formatMoney(cents) {
      const format = this.moneyFormat;
      let value = Math.round(Number(cents) || 0);
      const fmt = (number, precision, thousands, decimal) => {
        thousands = thousands || ',';
        decimal = decimal || '.';
        const fixed = (number / 100).toFixed(precision);
        const parts = fixed.split('.');
        const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
        const rest = parts[1] ? decimal + parts[1] : '';
        return dollars + rest;
      };
      return format.replace(/\{\{\s*(\w+)\s*\}\}/, (m, name) => {
        switch (name) {
          case 'amount': return fmt(value, 2);
          case 'amount_no_decimals': return fmt(value, 0);
          case 'amount_with_comma_separator': return fmt(value, 2, '.', ',');
          case 'amount_no_decimals_with_comma_separator': return fmt(value, 0, '.', ',');
          case 'amount_with_space_separator': return fmt(value, 2, ' ', ',');
          case 'amount_no_decimals_with_space_separator': return fmt(value, 0, ' ', ',');
          case 'amount_with_apostrophe_separator': return fmt(value, 2, "'", '.');
          default: return fmt(value, 2);
        }
      });
    }
  }
  customElements.define('c-bundle', CBundle);
}
